import React, { useContext, useEffect, useState } from 'react';

import ImagePCAScatterPlot from './ImagePCAScatterPlot';
import ClusterPairExplorer from './ClusterPairExplorer';
import { loadClusterItems } from './clusterItemsLoader.js';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';

/**
 * Plots the embeddings of the selected clusters together. With exactly two clusters it delegates to
 * ClusterPairExplorer (scatter with the Fisher discriminant view + the cross-cluster pair grid);
 * with more it loads every cluster's embeddings and shows one combined PCA scatter colored by
 * cluster. Embeddings come from the shared clusterItemsLoader, so no loader prop is needed.
 * Driven from ImgClustersManager's multi-selection and the cluster-links page.
 *
 * @param {Array<string|number>} clusterIds  the selected clusters
 * @param {Array<string>} [modelIds]  model id per cluster (aligned with clusterIds), shown in the pair view
 */
export default function MultiClusterScatterModal({ clusterIds, modelIds = [] }) {
  const isPair = clusterIds.length === 2;
  const { page } = useContext(LegoAdminPageContext);
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isPair) return undefined; // the pair case is handled by ClusterPairExplorer below
    let alive = true;
    setError(null);
    setItems(null);
    Promise.all(clusterIds.map((id) => loadClusterItems(page, id)))
      .then((lists) => alive && setItems(lists.flat()))
      .catch((err) => alive && setError(String(err?.message || err)));
    return () => { alive = false; };
  }, [String(clusterIds), isPair, page]);

  if (isPair) {
    return (
      <ClusterPairExplorer
        clusterIdA={String(clusterIds[0])}
        clusterIdB={String(clusterIds[1])}
        modelIdA={modelIds[0] || ''}
        modelIdB={modelIds[1] || ''}
        height="calc(100vh - 160px)"
      />
    );
  }

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!items) return <div className="alert alert-info">Loading embeddings…</div>;
  if (!items.length) return <div className="alert alert-secondary">No embeddings available for the selected clusters.</div>;

  return (
    <div style={{ height: 'calc(100vh - 160px)', minHeight: 460 }}>
      <ImagePCAScatterPlot
        items={items}
        enableDiscriminant
        markClosestCrossCluster
        defaultShowNnEdges={true}
        title={`${clusterIds.length} clusters combined (${items.length} imgs).`}
      />
    </div>
  );
}
